/**
 * Pure helpers for the Friends of Scouting screens: request ids, error shapes, scope keys, initials and dates. No Firebase
 * import, so they are tested directly in Node (scripts/test-fos-client.ts).
 */
import type { ScopeInput } from './fosApi';

export const newRequestId = (): string => (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`).replace(/[^A-Za-z0-9_-]/g, '-');

export interface FosCallError { message: string; code?: string; details?: { code?: string; candidates?: Array<{ donorId: string; ref: string; displayName: string; kind: string; reasons: string[] }> } }
export function fosError(cause: unknown): FosCallError {
  const e = cause as { message?: string; details?: FosCallError['details'] } | null;
  const raw = e?.message ?? 'Something went wrong. Please try again.';
  return { message: raw.replace(/^Error:\s*/, ''), details: e?.details, code: e?.details?.code };
}
export const isVersionConflict = (e: FosCallError) => e.code === 'FOS_VERSION_CONFLICT';
/** A financial read was refused until the user acknowledges that their access is logged (nothing was delivered). */
export const isFinancialAckRequired = (e: FosCallError) => e.code === 'FOS_FINANCIAL_ACK_REQUIRED';
export const isTimeZoneRequired = (e: FosCallError) => e.code === 'FOS_TIME_ZONE_REQUIRED';

/** The council-local day in a time zone, as the server computes it (YYYY-MM-DD); used only to label what the user sees. */
export function councilDay(timeZone: string, at: Date): string {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(at);
  const get = (type: string) => parts.find((p) => p.type === type)!.value;
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** IANA zones the browser knows, with the browser's own zone first; falls back to a short list on older browsers. */
export function timeZoneChoices(): string[] {
  const own = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return ''; } })();
  const all = (() => { try { return (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone') ?? []; } catch { return []; } })();
  const base = all.length ? all : ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu', 'UTC'];
  return [...new Set([own, ...base].filter(Boolean))];
}

export const scopeFromKey = (scopeKey: string): ScopeInput => scopeKey === 'council' ? { kind: 'council' } : { kind: 'district', districtId: scopeKey.replace(/^district:/, '') };
export const initials = (name: string) => name.split(/\s+/).filter((w) => /[\p{L}\p{N}]/u.test(w)).slice(0, 2).map((w) => (w.match(/[\p{L}\p{N}]/u)?.[0] ?? '').toUpperCase()).join('') || '?';
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Whole-cent money for display: $1,234.56 (no rounding of cents; amounts are integers end to end). */
export function formatMoney(cents: number, opts: { compact?: boolean } = {}): string {
  const dollars = cents / 100;
  if (opts.compact && Math.abs(dollars) >= 10_000) return `$${Math.round(dollars / 1000).toLocaleString('en-US')}k`;
  const whole = Number.isInteger(dollars);
  return dollars.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 });
}

/** Parses what a person types ("1250", "$1,250.50") into exact cents, or null when it is not a clear positive amount. */
export function parseMoney(input: string): number | null {
  const m = /^(\d{1,10})(?:\.(\d{1,2}))?$/.exec(String(input).trim().replace(/^\$/, '').replace(/,/g, ''));
  if (!m) return null;
  const cents = Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0') || '0');
  return cents > 0 ? cents : null;
}

/** Today in the browser's own calendar (YYYY-MM-DD), used only to pre-fill and bound date fields; the server decides. */
export function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * The widths (percent of the bar) for a progress bar. With a goal the whole bar IS the goal, so bars for different districts compare
 * directly and the goal line is always at the end; money beyond the goal is cut off and `over` is true. Without a goal the bar shows the
 * composition of what exists. Segments never overlap and never exceed 100%.
 */
export function barSegments(input: { raised: number; pending: number; outstanding: number; goal: number | null }): { raised: number; pending: number; outstanding: number; over: boolean } {
  const total = input.raised + input.pending + input.outstanding;
  const scale = input.goal && input.goal > 0 ? input.goal : Math.max(total, 1);
  const raised = Math.min(input.raised, scale);
  const pending = Math.min(input.pending, scale - raised);
  const outstanding = Math.min(input.outstanding, scale - raised - pending);
  const pct = (n: number) => Math.max(0, (n / scale) * 100);
  return { raised: pct(raised), pending: pct(pending), outstanding: pct(outstanding), over: !!input.goal && total > input.goal };
}
